import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, Instagram, Facebook, Mail, Globe, ExternalLink } from 'lucide-react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NF_WEBSITE = "https://www.nfconstruction.co.nz";
const INSTAGRAM_URL = "https://www.instagram.com/nfconstruction_/";
const FACEBOOK_URL = "https://www.facebook.com/NFConstructionNZ/";

const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_mangawhai-126/artifacts/8u4epx81_ChatGPT%20Image%20Apr%2010%2C%202026%20at%2012_59_32%20PM.png";
const PROJECT_IMAGE = "https://customer-assets.emergentagent.com/job_mangawhai-126/artifacts/qlfddatu_RENDER%2002_HD.png";
const INTERIOR_IMAGE = "https://customer-assets.emergentagent.com/job_mangawhai-126/artifacts/8rcil8x0_RENDER%2004_HD.png";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 1, ease: [0.22, 1, 0.36, 1] }
};

const fadeIn = {
  initial: { opacity: 0 },
  whileInView: { opacity: 1 },
  viewport: { once: true },
  transition: { duration: 1.2 }
};

const AboutNF = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/enquiry`, { ...formData, source_page: 'about-nf-construction' });
      alert('Thank you for your enquiry. We will be in touch.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      alert('There was an error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1a1e1a]">
      <Header />

      {/* Hero */}
      <section className="relative h-[70vh] flex items-end">
        <div className="absolute inset-0 z-0">
          <img src={HERO_IMAGE} alt="NF Construction" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1a1e1a] via-[#1a1e1a]/50 to-transparent" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 pb-16 md:pb-24">
          <motion.h1 {...fadeUp} className="text-4xl md:text-6xl text-white font-light leading-tight">
            Award-Winning Builders<br />
            <span className="italic font-accent text-[#c8bfa8]">Master Builders Gold</span>
          </motion.h1>
        </div>
      </section>

      {/* About NF Construction */}
      <section className="py-32 md:py-40 bg-[#1a1e1a]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.p {...fadeUp} className="text-[#c8bfa8] text-sm tracking-[0.3em] uppercase mb-8">
            NF Construction
          </motion.p>
          <motion.h2 {...fadeUp} className="text-3xl md:text-4xl text-white font-light mb-8">
            The builder of The 126 House
          </motion.h2>
          <motion.div {...fadeUp} className="space-y-6 text-white/80 text-lg leading-relaxed">
            <p>
              NF Construction builds high-end architectural homes, working alongside leading architects —
              including Wingates, the architect of The 126 House.
            </p>
            <p>
              Our homes have been recognised in the Registered Master Builders House of the Year awards
              since our first Gold Award in 2019: Gold Awards in 2019, 2023, 2024 and 2025; Top 100 in
              2022, 2024 and 2025; and a Regional Category Win in 2022.
            </p>
            <p>
              Known for building homes that respond to both landscape and lifestyle, the team brings 
              a refined, hands-on approach to every project.
            </p>
          </motion.div>
          
          <motion.div {...fadeUp} className="mt-12">
            <a
              href={NF_WEBSITE}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#c8bfa8] text-sm tracking-[0.15em] uppercase border-b border-[#c8bfa8]/50 pb-1 hover:border-[#c8bfa8] transition-colors"
            >
              Visit NF Construction
              <ExternalLink size={14} />
            </a>
          </motion.div>

          {/* Awards */}
          <motion.div {...fadeUp} className="mt-20 pt-12 border-t border-white/10">
            <p className="text-[#c8bfa8] text-xs tracking-[0.3em] uppercase mb-8 text-center">
              Recognition
            </p>
            <img
              src="https://customer-assets.emergentagent.com/job_mangawhai-126/artifacts/e7lb7uqv_Awards%20.png"
              alt="NF Construction — Master Builders House of the Year awards"
              className="block w-full h-auto"
              data-testid="awards-image"
            />
          </motion.div>
        </div>
      </section>

      {/* Full bleed interior */}
      <motion.section {...fadeIn} className="h-[60vh] relative">
        <img src={INTERIOR_IMAGE} alt="NF Construction interior" className="w-full h-full object-cover" />
      </motion.section>

      {/* Nic Foster Bio */}
      <section className="py-32 md:py-40 bg-[#3d4f3c]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.p {...fadeUp} className="text-[#c8bfa8] text-sm tracking-[0.3em] uppercase mb-8">
            The Founder
          </motion.p>
          <motion.h2 {...fadeUp} className="text-3xl md:text-4xl text-white font-light mb-8">
            Nic Foster — Founder
          </motion.h2>
          <motion.div {...fadeUp} className="space-y-6 text-white/80 text-lg leading-relaxed">
            <p>
              Nic Foster is driven by a deep passion for architecture and the way it shapes how people live.
            </p>
            <p>
              With a focus on clean design, natural materials, and timeless form, Nic approaches every 
              home as a balance between function, environment, and beauty.
            </p>
            <p>
              His work reflects a commitment to creating spaces that not only look exceptional but feel 
              considered, calm, and enduring.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Current Project */}
      <section className="py-32 md:py-40 bg-[#1a1e1a]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeUp}>
              <img src={PROJECT_IMAGE} alt="The 126 House" className="w-full h-auto" />
            </motion.div>
            <motion.div {...fadeUp}>
              <p className="text-[#c8bfa8] text-sm tracking-[0.3em] uppercase mb-6">Current Project</p>
              <h2 className="text-3xl md:text-4xl text-white font-light mb-6">The 126 House</h2>
              <p className="text-white/80 leading-relaxed mb-4">
                An architectural coastal home at 126 Robert Hastie Drive, The Sanctuary, Mangawhai Heads — designed by Wingates, built by NF Construction. Asking price NZ$3.8M.
              </p>
              <p className="text-white/80 leading-relaxed mb-8">
                Showcasing exceptional architectural design, premium materials, and a seamless 
                connection between indoor and outdoor living.
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-[#c8bfa8] text-sm tracking-[0.15em] uppercase border-b border-[#c8bfa8]/50 pb-1 hover:border-[#c8bfa8] transition-colors"
              >
                View The 126 House
                <ArrowRight size={14} />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-32 md:py-40 bg-[#3d4f3c]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <motion.div {...fadeUp}>
              <p className="text-[#c8bfa8] text-sm tracking-[0.3em] uppercase mb-6">Contact</p>
              <h2 className="text-3xl md:text-4xl text-white font-light mb-6">
                Let's Build Together
              </h2>
              <p className="text-white/80 leading-relaxed mb-8">
                Ready to start your architectural build? Get in touch with NF Construction.
              </p>
              <div className="space-y-4 mb-8">
                <a href={NF_WEBSITE} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-white/80 hover:text-white text-sm transition-colors">
                  <Globe size={16} /> nfconstruction.co.nz
                </a>
                <a href="mailto:info@nfconstruction.co.nz" className="flex items-center gap-3 text-white/80 hover:text-white text-sm transition-colors">
                  <Mail size={16} /> info@nfconstruction.co.nz
                </a>
              </div>
              <div className="flex gap-4">
                <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="text-white/40 hover:text-white transition-colors">
                  <Instagram size={18} />
                </a>
                <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" className="text-white/40 hover:text-white transition-colors">
                  <Facebook size={18} />
                </a>
              </div>
            </motion.div>

            <motion.form {...fadeUp} onSubmit={handleSubmit} className="space-y-6">
              <Input
                type="text"
                placeholder="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-4 text-white placeholder:text-white/30 focus-visible:ring-0 focus-visible:border-[#c8bfa8]"
                required
              />
              <Input
                type="email"
                placeholder="Email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-4 text-white placeholder:text-white/30 focus-visible:ring-0 focus-visible:border-[#c8bfa8]"
                required
              />
              <Textarea
                placeholder="Tell us about your project"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                rows={3}
                className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-4 text-white placeholder:text-white/30 focus-visible:ring-0 focus-visible:border-[#c8bfa8] resize-none"
              />
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-[#c8bfa8] text-[#1a1e1a] px-10 py-3 rounded-none text-sm tracking-[0.15em] uppercase hover:bg-white transition-colors"
              >
                {isSubmitting ? 'Submitting...' : 'Send Enquiry'}
              </Button>
            </motion.form>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutNF;
